import { ref, watch } from 'vue'

export type ConsentStatus = 'pending' | 'accepted' | 'rejected'

const STORAGE_KEY = 'cookie-consent'

// Estado global compartido
const consentStatus = ref<ConsentStatus>('pending')
const showBanner = ref(false)

// Inicializar desde localStorage
const initConsent = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const data = JSON.parse(stored)
      consentStatus.value = data.status
      showBanner.value = false
      
      // Aplicar el consentimiento guardado a Google
      if (data.status === 'accepted') {
        updateGoogleConsent(true)
      } else if (data.status === 'rejected') {
        updateGoogleConsent(false)
      }
    } else {
      showBanner.value = true
    }
  } catch (error) {
    console.error('Error loading cookie consent:', error)
    showBanner.value = true
  }
}

// Guardar en localStorage
const saveConsent = (status: ConsentStatus) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      status,
      timestamp: new Date().toISOString()
    }))
  } catch (error) {
    console.error('Error saving cookie consent:', error)
  }
}

// Función para actualizar el consentimiento de Google
const updateGoogleConsent = (granted: boolean) => {
  if (typeof window !== 'undefined' && (window as any).dataLayer) {
    const consentValue = granted ? 'granted' : 'denied'
    ;(window as any).dataLayer.push({
      'event': 'consent_update',
      'consent': {
        'ad_storage': consentValue,
        'ad_user_data': consentValue,
        'ad_personalization': consentValue,
        'analytics_storage': consentValue
      }
    })
    
    // También actualizar usando gtag si está disponible (por compatibilidad)
    if (typeof (window as any).gtag === 'function') {
      ;(window as any).gtag('consent', 'update', {
        'ad_storage': consentValue,
        'ad_user_data': consentValue,
        'ad_personalization': consentValue,
        'analytics_storage': consentValue
      })
    }
  }
}

// Watch para guardar cambios automáticamente
watch(consentStatus, (newStatus) => {
  if (newStatus !== 'pending') {
    saveConsent(newStatus)
  }
})

export const useCookieConsent = () => {
  const acceptCookies = () => {
    consentStatus.value = 'accepted'
    showBanner.value = false
    
    // Actualizar Google Consent Mode
    updateGoogleConsent(true)
    
    // Enviar evento a GTM
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      ;(window as any).dataLayer.push({
        event: 'cookie_consent_accepted'
      })
    }
  }

  const rejectCookies = () => {
    consentStatus.value = 'rejected'
    showBanner.value = false
    
    // Actualizar Google Consent Mode
    updateGoogleConsent(false)
    
    // Enviar evento a GTM
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      ;(window as any).dataLayer.push({
        event: 'cookie_consent_rejected'
      })
    }
  }

  const resetConsent = () => {
    localStorage.removeItem(STORAGE_KEY)
    consentStatus.value = 'pending'
    showBanner.value = true
  }

  return {
    consentStatus,
    showBanner,
    acceptCookies,
    rejectCookies,
    resetConsent,
    initConsent
  }
}
